import { columnTarget, tourTarget } from "components/common/Tour";
import { Tip } from "components/common/Tour/Tip";
import { Tour, TourStepData } from "components/common/Tour/types";
import * as s from "./styles";
import { TOUR_ANCHORS } from "./types";

// `Table` and `TableCollapsible` mark their <table> with this test id, so the
// tour can point inside them without anchors of its own there
const TABLE = '[data-testid="table-container"]';
const SUB_USERS_TABLE = `${tourTarget(TOUR_ANCHORS.SUB_USERS_TABLE)} ${TABLE}`;

// the header cell of the Access column: after the expand arrow, the name and
// the principal ARN; the tour highlights the whole column from it. Matches
// only once the table has subusers, an empty one has no column to point at
const ACCESS_COLUMN_HEADER = `${SUB_USERS_TABLE}:has(> tbody > tr > [data-testid="table-row-actions-menu-button"]) > thead th:nth-child(4)`;
// matches the arrow of every subuser that has keys, so a click on any of
// them counts; the spotlight shows the first one
const EXPAND_SUB_USER_BUTTON = `${SUB_USERS_TABLE} > tbody > tr > td:first-child > button:not([disabled])`;
const SUB_USER_KEYS_TABLE = `${SUB_USERS_TABLE} > tbody table`;
// the actions menu of a subuser row; the spotlight shows the first one
const SUB_USER_ACTIONS = `${SUB_USERS_TABLE} > tbody > tr > [data-testid="table-row-actions-menu-button"]`;
// the actions menu of a credentials row; the spotlight shows the first one
const CREDENTIALS_ACTIONS = `${tourTarget(TOUR_ANCHORS.CREDENTIALS_TABLE)} ${TABLE} > tbody > tr > [data-testid="table-row-actions-menu-button"]`;
const SUB_USER_KEY_ACTIONS = `${SUB_USER_KEYS_TABLE} [data-testid="table-collapsible-row-actions-menu-button"]`;

// FormDialog marks every toggle button with its field name and value, so the
// Access field of the create subuser dialog is the group around them
const getAccessField = (): HTMLElement | null =>
  document.querySelector('[data-testid="dialog-toggle-button-access-read"]')
    ?.parentElement ?? null;

const DIALOG_SUBMIT_BUTTON = '[data-testid="dialog-submit-button"]';

// placeholders stay in the examples: the tour is the same for every project
const OWNER_ARN = "arn:aws:iam::<PROJECT_ID>:user/<PROJECT_ID>";
const SUB_USER_ARN = `${OWNER_ARN}:<SUBUSER_NAME>`;
const BUCKET_ARN = "arn:aws:s3:::<BUCKET_NAME>";
const BUCKET_OBJECTS_ARN = `${BUCKET_ARN}/*`;

const ONE_BUCKET_POLICY = {
  Version: "2012-10-17",
  Statement: [
    {
      Sid: "SubuserListBucket",
      Effect: "Allow",
      Principal: { AWS: [SUB_USER_ARN] },
      Action: ["s3:ListBucket"],
      Resource: [BUCKET_ARN]
    },
    {
      Sid: "SubuserObjectReadWrite",
      Effect: "Allow",
      Principal: { AWS: [SUB_USER_ARN] },
      Action: ["s3:GetObject", "s3:PutObject", "s3:DeleteObject"],
      Resource: [BUCKET_OBJECTS_ARN]
    }
  ]
};

const ALLOWED_IPS_POLICY = {
  Version: "2012-10-17",
  Statement: [
    {
      Sid: "DenyRequestsOutsideAllowedIPs",
      Effect: "Deny",
      Principal: "*",
      Action: "s3:*",
      Resource: [BUCKET_ARN, BUCKET_OBJECTS_ARN],
      Condition: {
        NotIpAddress: {
          "aws:SourceIp": ["<OFFICE_IP_CIDR>", "<VPN_IP_CIDR>"]
        }
      }
    }
  ]
};

const READ_ONLY_EXCEPT_OWNER_POLICY = {
  Version: "2012-10-17",
  Statement: [
    {
      Sid: "AllowReadOnlyForEveryone",
      Effect: "Allow",
      Principal: "*",
      Action: ["s3:GetObject", "s3:ListBucket"],
      Resource: [BUCKET_ARN, BUCKET_OBJECTS_ARN]
    },
    {
      Sid: "DenyWriteDeleteExceptOwner",
      Effect: "Deny",
      NotPrincipal: { AWS: [OWNER_ARN] },
      Action: [
        "s3:PutObject",
        "s3:DeleteObject",
        "s3:PutObjectAcl",
        "s3:PutBucketPolicy",
        "s3:PutBucketAcl",
        "s3:DeleteBucket"
      ],
      Resource: [BUCKET_ARN, BUCKET_OBJECTS_ARN]
    }
  ]
};

const formatPolicy = (policy: object) => JSON.stringify(policy, null, 2);

// the tour only shows the dialog, so it cancels it when moving on
const closeDialog = () =>
  document
    .querySelector<HTMLElement>('[data-testid="dialog-secondary-button"]')
    ?.click();

// the steps before the buckets one are on the credentials tab, so going back
// from it opens that tab again
const openCredentialsTab = () =>
  document
    .querySelector<HTMLElement>(tourTarget(TOUR_ANCHORS.CREDENTIALS_TAB))
    ?.click();

// the subuser keys step asks to expand a row, so going back to it collapses
// the rows the user expanded
const collapseSubUser = () =>
  document
    .querySelectorAll<HTMLElement>(
      `${EXPAND_SUB_USER_BUTTON}[aria-expanded="true"]`
    )
    .forEach((button) => button.click());

// the first steps are about the top of the page, while the page can be left
// scrolled down (e.g. by the end of a previous run), so they scroll it up
// The step waits for the smooth scroll to end, so the tooltip is placed on
// the scrolled page. `scrollend` is missing in older Safari, hence the timeout.
const scrollToTop = () =>
  new Promise<void>((resolve) => {
    if (window.scrollY === 0) {
      resolve();
      return;
    }
    const timeout = window.setTimeout(done, 1000);
    function done() {
      window.clearTimeout(timeout);
      window.removeEventListener("scrollend", done);
      resolve();
    }
    window.addEventListener("scrollend", done);
    window.scrollTo({ top: 0, behavior: "smooth" });
  });

const introStep: Tour[number] = {
  target: "body",
  placement: "center",
  // the subuser keys step asks to expand a row, so the tour starts with all
  // rows collapsed, or that click would collapse a row expanded before it
  before: () => {
    collapseSubUser();
    return scrollToTop();
  },
  title: "Object Storage",
  content: (
    <>
      {`S3-compatible storage for files of any kind: backups, media, logs, static assets.\nData lives in buckets, and every request to them is signed with an access key and its secret.\n\nThis page manages those keys and who may use them.`}
      <Tip>
        {
          "Any S3 tool works with it: the AWS CLI, AWS SDKs, rclone, s3cmd and others."
        }
      </Tip>
    </>
  )
};

// shown whether the project has credentials or not
const credentialsTabStep: Tour[number] = {
  target: tourTarget(TOUR_ANCHORS.CREDENTIALS_TAB),
  placement: "bottom",
  before: scrollToTop,
  title: "Credentials Tab",
  content: (
    <>
      {"This tab contains the project's credentials: "}
      <em>{"master keys"}</em>
      {" and "}
      <em>{"subusers"}</em>
      {" with their own access keys."}
    </>
  )
};

// shown when the project has no credentials yet: the tour ends on creating
// them, and the page starts `credentialsTour` once they are in the table
const createCredentialsSteps: Tour = [
  {
    target: tourTarget(TOUR_ANCHORS.CREATE_CREDENTIALS),
    placement: "bottom",
    title: "Start with a master key",
    content: (
      <>
        {"This project doesn't have a "}
        <em>{"master key"}</em>
        {" yet.\nCreate a "}
        <em>{"master key"}</em>
        {" to start using the available features."}
      </>
    ),
    data: { advanceOnTargetClick: true } satisfies TourStepData
  },
  {
    target: DIALOG_SUBMIT_BUTTON,
    placement: "top",
    // the dialog keeps focus inside itself, a second focus trap would fight it
    disableFocusTrap: true,
    title: "Confirm",
    content: (
      <>
        {"Click "}
        <strong>{"Create"}</strong>
        {`. The secret will appear in the next window.\nSave it, then close the window to continue the tour.`}
        <Tip>
          {
            "You can view the secret again later from the master key row actions icon."
          }
        </Tip>
      </>
    ),
    data: {
      isDeferred: true,
      advanceOnTargetClick: true
    } satisfies TourStepData
  }
];

// the principal ARN and the endpoint are only rendered once the project has a
// master key
const summarySteps: Tour = [
  {
    target: tourTarget(TOUR_ANCHORS.PRINCIPAL_ARN),
    placement: "bottom",
    before: scrollToTop,
    title: "Principal ARN",
    content: (
      <>
        {
          "The unique identifier of the project's S3 user. Use it in bucket policies to grant or deny this user access to a bucket."
        }
        <Tip>
          {"The principal ARN is generated using this format: "}
          <strong>{"arn:aws:iam::[project_id]:user/[project_id]"}</strong>
        </Tip>
      </>
    )
  },
  {
    target: tourTarget(TOUR_ANCHORS.ENDPOINT),
    placement: "bottom",
    before: scrollToTop,
    title: "Endpoint",
    content:
      "The address your S3 tools connect to.\nIt is the same for all keys in this project."
  }
];

// the Browser tab comes last, once everything on the credentials tab is shown
const credentialsTabSteps: Tour = [
  {
    target: `${tourTarget(TOUR_ANCHORS.CREDENTIALS_TABLE)} ${TABLE}`,
    placement: "bottom",
    before: scrollToTop,
    title: "Master keys",
    content: (
      <>
        {`The project's master keys. They provide full access to all buckets and objects in the project\n\nYou can create as many master keys as you need.`}
      </>
    )
  },
  {
    target: CREDENTIALS_ACTIONS,
    placement: "left",
    title: "Master key available actions",
    content: (
      <>
        {"Use the row "}
        <strong>{"actions icon"}</strong>
        {" to manage the master key:\n\n"}
        <strong>{"Show access secret"}</strong>
        {
          " — view the secret associated with the access key. Both are required to connect to S3.\n"
        }
        <strong>{"Re-generate secret"}</strong>
        {" — replace the secret with a new one.\n"}
        <strong>{"Delete"}</strong>
        {" — delete the master key."}
      </>
    )
  },
  {
    target: tourTarget(TOUR_ANCHORS.SUB_USERS_TABLE),
    placement: "top",
    skipScroll: false,
    title: "Subusers",
    content: (
      <>
        {"A "}
        <em>{"subuser"}</em>
        {
          " is a separate identity with its own credentials and access level. It can access the project's buckets only according to the permissions assigned to it.\n\nYou can create as many subusers as you need, for example, for different applications or users.\n\n"
        }
        <strong>{"Main key"}</strong>
        {" — owns the buckets, can do everything, one per project.\n"}
        <strong>{"Subuser"}</strong>
        {
          " — limited to its access level, as many as you need, and deleting one does not affect the others."
        }
        <Tip>
          {
            "Deleting a subuser also deletes all of its credentials, so anything using them will immediately lose access."
          }
        </Tip>
      </>
    )
  },
  {
    target: tourTarget(TOUR_ANCHORS.CREATE_SUB_USER),
    placement: "bottom",
    skipScroll: false,
    title: "Create a subuser",
    content: (
      <>
        {"Click "}
        <strong>{"Create subuser"}</strong>
        {" to open the form.\nNext, we'll look at the available access levels."}
      </>
    ),
    data: { advanceOnTargetClick: true } satisfies TourStepData
  },
  {
    target: getAccessField,
    placement: "right",
    // the dialog keeps focus inside itself, a second focus trap would fight it
    disableFocusTrap: true,
    after: closeDialog,
    title: "Access levels",
    content: (
      <>
        {"Choose what the subuser can do across the project's buckets.\n\n"}
        <strong>{"None"}</strong>
        {
          " — no access by default. Use bucket policies to grant access to specific buckets.\n"
        }
        <strong>{"Read"}</strong>
        {
          " — view buckets and objects, and download files, but not upload, modify or delete them.\n"
        }
        <strong>{"Write"}</strong>
        {
          " — upload and delete objects, but not read or download them by default.\n"
        }
        <strong>{"Read & write"}</strong>
        {
          " — view, download, upload and delete objects, without managing bucket policies.\n"
        }
        <strong>{"Full"}</strong>
        {
          " — all read and write permissions, plus manage bucket settings and policies."
        }
        {
          "\n\nThis form will close when you continue the tour. No subuser will be created."
        }
      </>
    ),
    data: { isDeferred: true } satisfies TourStepData
  },
  {
    target: columnTarget(ACCESS_COLUMN_HEADER),
    placement: "left",
    skipScroll: false,
    title: "Finer control: bucket policies",
    content: (
      <>
        {"A subuser's access level applies to "}
        <em>{"all buckets"}</em>
        {
          ". For more precise control, use bucket policies to define access to individual buckets.\n\n"
        }
        <em>{"Typical uses:"}</em>
        {"\n"}
        {"• give a subuser access to one bucket only\n"}
        {"• block requests that do not come from your office or VPN IPs\n"}
        {"• keep one bucket read-only for everyone except its owner\n\n"}
        {
          "Bucket policies are configured through the S3 API, for example using the AWS CLI. You can view the current policy from the bucket details page.\n\nThe next steps show a few example policies."
        }
      </>
    ),
    // the access levels step points at the dialog, closed by now
    // without subusers it is shown in the middle, like the policy examples
    data: { backSteps: 2, centerIfTargetMissing: true } satisfies TourStepData
  },
  {
    // an example, not a part of the page, so it is shown in the middle
    target: "body",
    placement: "center",
    title: "Example: one bucket for a subuser",
    content: (
      <>
        {"Set the subuser's access level to "}
        <em>{"None"}</em>
        {
          ", then add this policy to the bucket.\nThe subuser will be able to list the bucket and read, upload and delete its objects, and will have no access to any other bucket."
        }
        <s.PolicyCode>{formatPolicy(ONE_BUCKET_POLICY)}</s.PolicyCode>
        <Tip>
          {
            "A subuser's ARN is the project's principal ARN followed by a colon and the subuser name."
          }
        </Tip>
      </>
    )
  },
  {
    // an example, not a part of the page, so it is shown in the middle
    target: "body",
    placement: "center",
    title: "Example: office and VPN only",
    content: (
      <>
        {
          "Denies every request to the bucket that does not come from the listed IP ranges, whatever key it is signed with."
        }
        <s.PolicyCode>{formatPolicy(ALLOWED_IPS_POLICY)}</s.PolicyCode>
        <Tip>
          {
            "Make sure the IP you apply the policy from is in the list, or the policy also locks you out of the bucket."
          }
        </Tip>
      </>
    )
  },
  {
    // an example, not a part of the page, so it is shown in the middle
    target: "body",
    placement: "center",
    title: "Example: read-only except the owner",
    content: (
      <>
        {
          "Anyone can list the bucket and download its objects, but only the project's master key can upload, delete or change the bucket's settings."
        }
        <s.PolicyCode>
          {formatPolicy(READ_ONLY_EXCEPT_OWNER_POLICY)}
        </s.PolicyCode>
        <Tip>
          {
            '"Principal": "*" includes anonymous requests, so this policy makes the bucket\'s objects public.'
          }
        </Tip>
      </>
    )
  },
  {
    target: SUB_USER_ACTIONS,
    placement: "left",
    skipScroll: false,
    title: "Subuser available actions",
    content: (
      <>
        {"Use the subuser row "}
        <em>{"actions icon"}</em>
        {" to \n\n"}
        <strong>{"Add key"}</strong>
        {" — create an additional key for this subuser.\n"}
        <strong>{"Delete subuser"}</strong>
        {" — remove the subuser and all of its keys."}
      </>
    )
  },
  {
    target: EXPAND_SUB_USER_BUTTON,
    placement: "right",
    skipScroll: false,
    title: "Subuser keys",
    content: (
      <>
        {"A subuser's keys are hidden by default. Click the "}
        <strong>{"arrow"}</strong>
        {" next to the subuser's name to view them."}
      </>
    ),
    data: { advanceOnTargetClick: true } satisfies TourStepData
  },
  {
    target: SUB_USER_KEYS_TABLE,
    placement: "top",
    skipScroll: false,
    title: "Why a subuser can have several keys",
    content: (
      <>
        {
          "All keys share the subuser's access level, but each can be managed separately.\n\nMultiple keys make it easy to rotate credentials without downtime: create a new key, update your application, then delete the old one."
        }
        <Tip>
          {
            "Give each application or environment its own key. If one leaks, delete just that key — the others keep working."
          }
        </Tip>
      </>
    ),
    data: { isDeferred: true, onBack: collapseSubUser } satisfies TourStepData
  },
  {
    target: SUB_USER_KEY_ACTIONS,
    placement: "left",
    skipScroll: false,
    title: "Key available actions",
    content: (
      <>
        {"Use the row "}
        <em>{"actions icon"}</em>
        {" to manage a key:\n\n"}
        <strong>{"Show access secret"}</strong>
        {
          " — view the secret associated with this access key. Both are required to connect to S3.\n"
        }
        <strong>{"Delete key"}</strong>
        {" — revokes only this key, the others keep working."}
      </>
    ),
    // back to the subuser keys step, past the steps that need the expanded row
    data: {
      isDeferred: true,
      backSteps: 2,
      onBack: collapseSubUser
    } satisfies TourStepData
  },
  {
    target: tourTarget(TOUR_ANCHORS.BROWSER_TAB),
    placement: "bottom",
    // the steps before it can leave the page scrolled down to the subusers
    skipScroll: false,
    title: "Browser Tab",
    content: (
      <>
        {"Now let's look at the buckets. Click the "}
        <strong>{"Browser"}</strong>
        {" tab to continue."}
      </>
    ),
    // back to the subuser keys step, past the steps that need the expanded row
    data: {
      advanceOnTargetClick: true,
      backSteps: 3,
      onBack: collapseSubUser
    } satisfies TourStepData
  },
  {
    // `Table` renders its body only once the buckets have loaded, so the
    // step waits for it instead of pointing at a loading table
    target: `${tourTarget(TOUR_ANCHORS.BUCKETS)} ${TABLE}:has(> [data-testid="table-body"])`,
    targetWaitTimeout: 15000,
    // the table shows its own loader meanwhile
    loaderComponent: null,
    placement: "top",
    skipScroll: false,
    title: "Your buckets",
    content: (
      <>
        {"View and manage all buckets in this project. Create a new one with "}
        <strong>{"Create bucket"}</strong>
        {", or use the row "}
        <strong>{"actions icon"}</strong>
        {" to manage an existing bucket.\n\nClick a "}
        <strong>{"bucket's name"}</strong>
        {" to open it and manage its files, folders, and retention policy."}
        {/* <Tip>
          {
            "A bucket can be deleted only when it is empty. Making it public or private overwrites its bucket policy."
          }
        </Tip> */}
      </>
    ),
    // back to the subuser keys step: the steps in between need the expanded
    // subuser row, which is collapsed again once the credentials tab reopens
    data: {
      isDeferred: true,
      backSteps: 4,
      onBack: openCredentialsTab
    } satisfies TourStepData
  }
];

// started by the page once the master key created during the tour appears
export const credentialsTour: Tour = [...summarySteps, ...credentialsTabSteps];

export const objectStorageTour: Tour = [
  introStep,
  ...summarySteps,
  credentialsTabStep,
  ...credentialsTabSteps
];

export const createCredentialsTour: Tour = [
  introStep,
  credentialsTabStep,
  ...createCredentialsSteps
];
